import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";

const OUTREACH_STATUS_COLORS: Record<string, string> = {
  not_contacted: "bg-slate-100 text-slate-600",
  dm_sent: "bg-blue-100 text-blue-700",
  replied: "bg-amber-100 text-amber-700",
  demo_scheduled: "bg-purple-100 text-purple-700",
  demo_done: "bg-indigo-100 text-indigo-700",
  closed_won: "bg-green-100 text-green-700",
  closed_lost: "bg-red-100 text-red-600",
  not_a_fit: "bg-gray-100 text-gray-500",
};

const OUTREACH_STATUS_LABELS: Record<string, string> = {
  not_contacted: "Not Contacted",
  dm_sent: "DM Sent",
  replied: "Replied",
  demo_scheduled: "Demo Scheduled",
  demo_done: "Demo Done",
  closed_won: "Closed Won ✓",
  closed_lost: "Closed Lost",
  not_a_fit: "Not a Fit",
};

const LEAD_SOURCE_LABELS: Record<string, string> = {
  google_maps: "Google Maps",
  apollo: "Apollo",
  linkedin: "LinkedIn",
  referral: "Referral",
  inbound: "Inbound",
  manual: "Manual",
};

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;
  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
      </div>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      {((record as any).outreach_status || (record as any).lead_source) && (
        <AsideSection title="Outreach">
          <div className="space-y-2 text-sm">
            {(record as any).outreach_status && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20 flex-shrink-0">Status</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    OUTREACH_STATUS_COLORS[(record as any).outreach_status] || "bg-slate-100 text-slate-600"
                  }`}
                >
                  {OUTREACH_STATUS_LABELS[(record as any).outreach_status] || (record as any).outreach_status}
                </span>
              </div>
            )}
            {(record as any).lead_source && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-20 flex-shrink-0">Source</span>
                <span className="text-foreground">
                  {LEAD_SOURCE_LABELS[(record as any).lead_source] || (record as any).lead_source}
                </span>
              </div>
            )}
            {(record as any).outreach_notes && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground">Notes</span>
                <p className="text-foreground text-xs leading-relaxed">
                  {(record as any).outreach_notes}
                </p>
              </div>
            )}
          </div>
        </AsideSection>
      )}

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};
